import {FC, useState, useEffect} from 'react';
import {Modal, Form, Tabs, Button, Steps} from 'antd';
import {CaretLeft, CaretRight} from 'phosphor-react';
import FormGroup from 'components/formItems/FormGroup';
import {NamePath} from 'antd/lib/form/interface';
import './styles.less';
import {ModalFormFieldTypes} from 'components/formItems/FormGroup/FormGroup';

const {TabPane} = Tabs;
const {Step} = Steps;

export const validateMessages = () => ({
	required: 'Պարտադիր դաշտ',
	types: {
		email: 'Սխալ ֆորմատ'
	}
});

interface ModalFormTypes {
	name: string;
	fields: ModalFormFieldTypes[];
	visibility?: Array<'ADD' | 'EDIT' | string>;
	errorStatus?: boolean;
}

interface ModalFormProps {
	title: string;
	visible: 'ADD' | 'EDIT' | 'VIEW' | false;
	onCreate?: (values: any, index: number) => void;
	onCancel: () => void;
	cancelText?: string;
	selectedRowIds?: number[];
	fields?: ModalFormFieldTypes[];
	modalForm?: ModalFormTypes[];
	editModalType: 'TAB' | 'STEP';
	addModalType: 'TAB' | 'STEP';
	formErrors: any;
	getFieldsValues?: (id: number) => Promise<any>;
	dialogWidth?: number;
	formInitialValues?: any;
	stepFinish?: boolean;
	onFormValuesChange?: (
		resetFields: (fields?: NamePath[]) => void,
		changedValues: any,
		allValues: any
	) => void;
	loading: boolean;
	formItemCustomInfo?: any;
}

const ModalForm: FC<ModalFormProps> = ({
	title,
	visible,
	onCreate,
	onCancel,
	cancelText,
	selectedRowIds,
	fields,
	modalForm,
	editModalType,
	addModalType,
	formErrors,
	getFieldsValues,
	dialogWidth = 1600,
	formInitialValues,
	stepFinish,
	onFormValuesChange,
	loading,
	formItemCustomInfo
}) => {
	const [current, setCurrent] = useState(0);
	const [formValues, setFormValues] = useState({});
	const [disableSubmit, setDisableSubmit] = useState(true);
	const [form] = Form.useForm();

	const visibleModalForms =
		visible && modalForm?.filter(form => visible === 'VIEW' || form.visibility?.includes(visible));
	const isStepper =
		modalForm &&
		((visible === 'ADD' && addModalType === 'STEP') || (visible === 'EDIT' && editModalType === 'STEP'));
	const isTab =
		modalForm &&
		((visible === 'EDIT' && editModalType === 'TAB') ||
			(visible === 'ADD' && addModalType === 'TAB') ||
			visible === 'VIEW');

	const prev = () => {
		setCurrent(current - 1);
	};

	const next = () => {
		form.validateFields().then(values => {
			setFormValues(prev => ({...prev, ...{...values}}));
			setCurrent(current + 1);
		});
	};

	const handleModalClose = () => {
		onCancel();
		form.resetFields();
		setCurrent(0);
	};

	const onTabChange = (activeKey: string) => {
		if (visible === 'EDIT') setDisableSubmit(true);
		setCurrent(+activeKey);
	};

	const onStepChange = (activeKey: number) => {
		setCurrent(activeKey);
	};

	const onValuesChange = (changedValues: any, allValues: any) => {
		if (visible === 'EDIT') setDisableSubmit(false);
		onFormValuesChange && onFormValuesChange(form.resetFields, changedValues, allValues);

		if (stepFinish) {
			setFormValues(prev => {
				const changedKey = Object.keys(changedValues)[0];
				if (
					changedKey === 'education' ||
					changedKey === 'admissionFiles' ||
					changedKey === 'universityExpectations'
				) {
					return {...prev, ...allValues};
				}

				return {...prev, ...changedValues};
			});
		}
	};

	const onSubmit = () => {
		form.validateFields().then(values => {
			if (isStepper) {
				const newForm = {...formValues, ...{...values}};
				onCreate && onCreate(newForm, current);
			} else {
				onCreate && onCreate(values, current);
			}
		});
	};

	useEffect(() => {
		if (visible === false) {
			form.resetFields();
			setCurrent(0);
		} else if (visible === 'ADD') setDisableSubmit(false);
		else if (visible === 'EDIT') setDisableSubmit(true);
	}, [visible]);

	useEffect(() => {
		if ((visible === 'EDIT' || visible === 'VIEW') && getFieldsValues && selectedRowIds?.length === 1) {
			getFieldsValues(selectedRowIds[0]).then(res => {
				const formData = res.data.data || res.data;
				for (let key in formInitialValues) {
					formData[key] =
						formData[key] === null || (Array.isArray(formData[key]) && formData[key]).length === 0
							? formInitialValues[key]
							: formData[key];
				}
				form.setFieldsValue(formData);
			});
		}
	}, [selectedRowIds, visible, current]);

	return (
		<Modal
			visible={!!visible}
			className="crud-modal"
			title={title}
			onCancel={handleModalClose}
			style={{top: 20}}
			width={dialogWidth}
			footer={[
				<Button className="main-btn" key="cancel" size="large" onClick={handleModalClose}>
					{cancelText || 'Չեղարկել'}
				</Button>,
				<div key="prev">
					{isStepper && !!visibleModalForms && current > 0 && (
						<Button className="main-btn" size="large" onClick={prev}>
							<CaretLeft size={14} weight="fill" style={{marginRight: 5}} /> Նախորդը
						</Button>
					)}
				</div>,
				<div key="next">
					{isStepper && !!visibleModalForms && current < visibleModalForms.length - 1 && (
						<Button className="main-btn" size="large" type="primary" onClick={next}>
							Հաջորդը <CaretRight size={14} weight="fill" style={{marginLeft: 5}} />
						</Button>
					)}
				</div>,
				<div key="submit">
					{visible !== 'VIEW' &&
						(fields ||
							!isStepper ||
							(!!visibleModalForms && current === visibleModalForms.length - 1)) &&
						onCreate && (
							<Button
								className="main-btn"
								size="large"
								type="primary"
								onClick={onSubmit}
								disabled={disableSubmit}
								loading={loading}>
								Պահպանել
							</Button>
						)}
				</div>
			]}>
			<Form
				form={form}
				layout="vertical"
				validateMessages={validateMessages()}
				initialValues={formInitialValues}
				onValuesChange={onValuesChange}>
				{!!fields && (
					<FormGroup
						tabFields={fields}
						formErrors={formErrors}
						visible={visible as 'ADD' | 'EDIT' | 'VIEW'}
						selectedRowId={selectedRowIds && selectedRowIds[0]}
						form={form}
						formItemCustomInfo={formItemCustomInfo}
					/>
				)}
				{isStepper && !!visibleModalForms && (
					<div className="form-steps">
						<Steps
							current={current}
							progressDot
							direction="vertical"
							size="small"
							onChange={stepFinish ? onStepChange : undefined}>
							{visibleModalForms.map((item, i) => (
								<Step
									key={i}
									title={item.name}
									status={item.errorStatus ? 'error' : stepFinish ? 'finish' : undefined}
								/>
							))}
						</Steps>
						<div className="steps-content">
							<FormGroup
								tabFields={visibleModalForms[current].fields}
								formErrors={formErrors}
								visible={visible}
								formInitialValues={formInitialValues}
								selectedRowId={selectedRowIds && selectedRowIds[0]}
								form={form}
								formItemCustomInfo={formItemCustomInfo}
							/>
						</div>
					</div>
				)}

				{isTab && !!visibleModalForms && (
					<Tabs
						tabPosition="left"
						destroyInactiveTabPane
						activeKey={current.toString()}
						onChange={onTabChange}>
						{visibleModalForms?.map((tab, i) => {
							return (
								<TabPane tab={tab.name} key={i}>
									<FormGroup
										tabFields={tab.fields}
										formErrors={formErrors}
										visible={visible}
										formInitialValues={formInitialValues}
										selectedRowId={selectedRowIds && selectedRowIds[0]}
										form={form}
										formItemCustomInfo={formItemCustomInfo}
									/>
								</TabPane>
							);
						})}
					</Tabs>
				)}
			</Form>
		</Modal>
	);
};

export default ModalForm;
